import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {isEmpty} from 'ramda'
import clsx from 'clsx'
import {lighten, makeStyles} from '@material-ui/core/styles'
import {
    Box,
    Button,
    IconButton,
    InputBase,
    Typography,
    Toolbar,
    Paper,
} from '@material-ui/core'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TablePagination from '@material-ui/core/TablePagination'
import TableRow from '@material-ui/core/TableRow'
import TableSortLabel from '@material-ui/core/TableSortLabel'
import Tooltip from '@material-ui/core/Tooltip'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Switch from '@material-ui/core/Switch'
import SuperAdminRolesDrawer from './SuperAdminRolesDrawer'
import ErrorBoundary from '../../../../components/ErrorBoundary'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'

//******** Images *************************
import SearchIcon from '@material-ui/icons/Search'
import Close from '@material-ui/icons/Close'
import DeleteIcon from '@material-ui/icons/Delete'
import PersonAddIcon from '@material-ui/icons/PersonAdd'
import selectEvent from '../../../../images/selectEvent.svg'

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1
    }
    if (b[orderBy] > a[orderBy]) {
        return 1
    }
    return 0
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy)
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index])
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0])
        if (order !== 0) return order
        return a[1] - b[1]
    })
    return stabilizedThis.map(el => el[0])
}

const headCells = [
    {
        id: 'euid',
        numeric: false,
        disablePadding: true,
        label: 'EUID',
    },
    {
        id: 'firstName',
        numeric: false,
        disablePadding: false,
        label: 'Name',
    },
]

function EnhancedTableHead(props) {
    const {classes, order, orderBy, onRequestSort} = props
    const createSortHandler = property => event => {
        onRequestSort(event, property)
    }

    return (
        <TableHead>
            <TableRow>
                {headCells.map(headCell => (
                    <TableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                        padding={headCell.disablePadding ? 'none' : 'default'}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <span className={classes.visuallyHidden}>
                                    {order === 'desc'
                                        ? 'sorted descending'
                                        : 'sorted ascending'}
                                </span>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
                <TableCell> </TableCell>
            </TableRow>
        </TableHead>
    )
}

EnhancedTableHead.propTypes = {
    classes: PropTypes.object.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
}

const useToolbarStyles = makeStyles(theme => ({
    root: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(1),
    },
    highlight:
        theme.palette.type === 'light'
            ? {
                  color: theme.palette.secondary.main,
                  backgroundColor: lighten(theme.palette.secondary.light, 0.85),
              }
            : {
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.secondary.dark,
              },
    title: {
        flex: '1 1 100%',
    },

    buttonlabel: {
        width: '400px',
        whiteSpace: 'nowrap',
    },
    filterlabel: {
        marginTop: '-5px',
    },
    searchBox: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        border: '1px solid',
        borderColor: theme.palette.grey.medium,
        borderRadius: theme.shape.borderRadius,
        margin: '0 0 0 5px',
        height: '35px',
    },
    searchInput: {
        flex: 1,
        width: '250px',
        margin: '0 10px',
        // [theme.breakpoints.down('sm')]: {
        //     width: '75%',
        // },
    },
    clearButton: {
        padding: 8,
        color: theme.palette.grey.medium,
    },
}))

const EnhancedTableToolbar = props => {
    const classes = useToolbarStyles()
    const {
        numSelected,
        setRow,
        searchTerm,
        setSearchTerm,
        handleClickOpen,
    } = props

    const handleChange = e => {
        setSearchTerm(e.target.value)
    }

    const handleClearSearch = () => {
        setSearchTerm('')
    }

    return (
        <Toolbar
            className={clsx(classes.root, {
                [classes.highlight]: numSelected > 0,
            })}
        >
            {numSelected > 0 ? (
                <Typography
                    className={classes.title}
                    color="inherit"
                    variant="subtitle1"
                    component="div"
                >
                    {numSelected} selected
                </Typography>
            ) : (
                <Typography
                    className={classes.title}
                    align="left"
                    variant="h4"
                    id="tableTitle"
                    component="div"
                >
                    Super Admins
                </Typography>
            )}

            {numSelected > 0 ? (
                <Tooltip title="Delete">
                    <IconButton aria-label="delete">
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            ) : (
                <>
                    <Button
                        variant="contained"
                        color="primary"
                        aria-label="create new event"
                        onClick={() => {
                            setRow({})
                            handleClickOpen('Create Super Admin')
                        }}
                        size="small"
                        startIcon={
                            <PersonAddIcon
                                style={{
                                    fill: '#ffffff',
                                    fontSize: '20px',
                                }}
                            />
                        }
                        style={{
                            margin: '0 5px',
                            height: '35px',
                            boxShadow: 'none',
                        }}
                        classes={{
                            label: classes.buttonlabel,
                        }}
                    >
                        ADD ADMIN
                    </Button>
                    <Box className={classes.searchBox}>
                        <InputBase
                            className={classes.searchInput}
                            placeholder="Search"
                            inputProps={{
                                'aria-label': 'search swipe teams',
                            }}
                            value={searchTerm}
                            onChange={handleChange}
                        />
                        {searchTerm && (
                            <IconButton
                                className={classes.clearButton}
                                onClick={() => handleClearSearch()}
                            >
                                {' '}
                                <Close />{' '}
                            </IconButton>
                        )}
                        {!searchTerm && (
                            <SearchIcon style={{margin: '0 5px'}} />
                        )}
                    </Box>
                </>
            )}
        </Toolbar>
    )
}

const useStyles = makeStyles(theme => ({
    root: {
        margin: '35px 10% 0 10%',
    },
    paper: {
        width: '100%',
        marginBottom: theme.spacing(2),
        boxShadow: 'none',
    },
    table: {
        minWidth: 750,
    },
    tablebody: {
        overflowY: 'auto',
        maxHeight: '65vh',
    },
    visuallyHidden: {
        border: 0,
        clip: 'rect(0 0 0 0)',
        height: 1,
        margin: -1,
        overflow: 'hidden',
        padding: 0,
        position: 'absolute',
        top: 20,
        width: 1,
    },
    emptycontainer: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        width: '-webkit-fill-available',
        height: '65vh',
        // [theme.breakpoints.down('sm')]: {
        //     minWidth: '300px',
        //     maxWidth: '90%',
        // },
    },
}))

export default function SuperAdminRolesTable(props) {
    const classes = useStyles()
    const [order, setOrder] = React.useState('asc')
    const [orderBy, setOrderBy] = React.useState('firstName')
    const [page, setPage] = React.useState(0)
    const [dense, setDense] = React.useState(true)
    const [rowsPerPage, setRowsPerPage] = React.useState(10)
    const [openDrawer, setOpenDrawer] = React.useState(false)
    const [title, setTitle] = React.useState(' ')
    const [row, setRow] = React.useState({})

    const {token, superadmins, loggedinUser, putSuperadmin} = props

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
    }

    const handleChangePage = (event, newPage) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = event => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const handleChangeDense = event => {
        setDense(event.target.checked)
    }

    const handleClickOpen = title => {
        setOpenDrawer(true)
        setTitle(title)
    }

    const handleCloseDrawer = () => {
        setOpenDrawer(false)
    }

    // Dialog parameters
    const [open, setOpen] = useState(false)

    const handleOpenDeleteDialog = superadmin => {
        setRow(superadmin)
        setOpen(true)
    }

    const handleClose = () => {
        setOpen(false)
    }

    const superadminarray = []
    superadminarray.push(row)
    const admin = false
    let payload = {
        superadminarray,
        admin,
    }

    const handleDelete = () => {
        putSuperadmin(token, payload)
        setOpen(false)
    }
    // End Dialog parameters

    const emptyRows =
        rowsPerPage -
        Math.min(rowsPerPage, superadmins.length - page * rowsPerPage)

    const [searchTerm, setSearchTerm] = useState('')
    const [searchResults, setSearchResults] = useState(superadmins)

    useEffect(() => {
        const results = superadmins.filter(
            superadmin =>
                superadmin.firstName
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase()) ||
                superadmin.lastName
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase())
        )
        setSearchResults(results)
    }, [superadmins, searchTerm])

    return (
        <div className={classes.root}>
            <Paper className={classes.paper}>
                <EnhancedTableToolbar
                    setRow={setRow}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    handleClickOpen={handleClickOpen}
                />

                {isEmpty(superadmins) ? (
                    <Box className={classes.emptycontainer}>
                        <img src={selectEvent} alt="select event" />
                        <Typography align="center" variant="h5">
                            Create a new Super Admin
                        </Typography>
                        <Typography align="center" variant="body1">
                            Click on the ADD ADMIN
                        </Typography>
                        <Typography align="center" variant="body1">
                            button above.
                        </Typography>
                    </Box>
                ) : (
                    <TableContainer>
                        <Table
                            className={classes.table}
                            aria-labelledby="tableTitle"
                            size={dense ? 'small' : 'medium'}
                            aria-label="enhanced table"
                        >
                            <EnhancedTableHead
                                classes={classes}
                                order={order}
                                orderBy={orderBy}
                                onRequestSort={handleRequestSort}
                                rowCount={superadmins.length}
                            />
                            <TableBody className={classes.tablebody}>
                                {stableSort(
                                    searchResults,
                                    getComparator(order, orderBy)
                                )
                                    .slice(
                                        page * rowsPerPage,
                                        page * rowsPerPage + rowsPerPage
                                    )
                                    .map(row => {
                                        return (
                                            <TableRow
                                                hover
                                                role="checkbox"
                                                tabIndex={-1}
                                                key={row.euid}
                                            >
                                                <TableCell
                                                    id={row.euid}
                                                    align="left"
                                                >
                                                    {row.euid}
                                                </TableCell>
                                                <TableCell
                                                    id={
                                                        row.firstName +
                                                        row.lastName
                                                    }
                                                    align="left"
                                                >
                                                    {row.firstName +
                                                        ' ' +
                                                        row.lastName}
                                                </TableCell>
                                                <TableCell>
                                                    <IconButton
                                                        onClick={event => {
                                                            event.stopPropagation()
                                                            handleOpenDeleteDialog(
                                                                row
                                                            )
                                                        }}
                                                        disabled={
                                                            loggedinUser ===
                                                            row.euid
                                                                ? true
                                                                : false
                                                        }
                                                    >
                                                        <DeleteIcon
                                                            style={
                                                                loggedinUser ===
                                                                row.euid
                                                                    ? {
                                                                          opacity: 0.5,
                                                                          color:
                                                                              '#098447',
                                                                      }
                                                                    : {
                                                                          color:
                                                                              '#098447',
                                                                      }
                                                            }
                                                        />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                {emptyRows > 0 && (
                                    <TableRow
                                        style={{
                                            height:
                                                (dense ? 33 : 53) * emptyRows,
                                        }}
                                    >
                                        <TableCell colSpan={6} />
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                {!isEmpty(superadmins) && (
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={superadmins.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onChangePage={handleChangePage}
                        onChangeRowsPerPage={handleChangeRowsPerPage}
                    />
                )}
            </Paper>
            {!isEmpty(superadmins) && (
                <FormControlLabel
                    control={
                        <Switch checked={dense} onChange={handleChangeDense} />
                    }
                    label="Dense padding"
                />
            )}
            <SuperAdminRolesDrawer
                title={title}
                currentsuperadmin={row}
                openDialog={openDrawer}
                handleCloseDialog={handleCloseDrawer}
            />
            <ErrorBoundary>
                <Dialog
                    aria-labelledby="Confirm deletion of super admin"
                    aria-describedby="Delete Super Admin Modal"
                    open={open}
                    scroll="paper"
                    fullScreen={window.innerWidth < 480}
                    onClose={handleClose}
                >
                    <DialogTitle id="confirmation-delete-super-admin">
                        Delete Super Admin?
                    </DialogTitle>
                    <DialogContent>
                        {row.euid +
                            ': ' +
                            row.firstName +
                            ' ' +
                            row.lastName +
                            ' will be removed. Continue?'}
                    </DialogContent>
                    <DialogActions>
                        <Button autoFocus onClick={handleClose} color="primary">
                            Cancel
                        </Button>
                        <Button onClick={handleDelete} color="primary">
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </ErrorBoundary>
        </div>
    )
}
